import { useState, useEffect, useCallback } from 'react';

import { Plus, RefreshCw } from 'lucide-react';

import AddUserModal from '@/components/admin/AddUserModal';
import UserTable from '@/components/admin/UserTable';
import { Button } from '@/components/ui/button';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import PageLayout from '@/layouts/PageLayout';
import { apiService } from '@/lib/axios';

const AdminPage = () => {
  const { canManageAllOrgs } = useAdminAccess();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.users.getAll();
      setUsers(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRefreshCache = async () => {
    setRefreshing(true);
    try {
      await apiService.users.refreshCache();
      await fetchUsers();
    } catch {
      setError('Failed to refresh cache');
    } finally {
      setRefreshing(false);
    }
  };

  const subtitle = canManageAllOrgs
    ? 'Managing all organizations'
    : 'Managing your organization';

  return (
    <PageLayout
      title="User Management"
      subtitle={subtitle}
      renderOptions={() => (
        <div className="flex gap-2">
          <Button onClick={() => setShowAddUser(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
          <Button variant="outline" onClick={handleRefreshCache} disabled={refreshing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Cache
          </Button>
        </div>
      )}
    >
      {loading && (
        <div className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <UserTable users={users} onRefresh={fetchUsers} />
      )}
      <AddUserModal
        open={showAddUser}
        onOpenChange={setShowAddUser}
        onSuccess={fetchUsers}
      />
    </PageLayout>
  );
};

export default AdminPage;
